import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface PanigationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  siblingCount?: number;
}

const range = (start: number, end: number) => {
  return Array.from({ length: end - start + 1 }, (_, index) => index + start);
};

const buildPaginationItems = (
  totalPages: number,
  currentPage: number,
  siblingCount: number,
) => {
  const safeTotalPages = Math.max(1, totalPages);
  const boundaryCount = 1;
  const totalPageNumbers = siblingCount * 2 + 3 + boundaryCount * 2;

  if (safeTotalPages <= totalPageNumbers) {
    return range(1, safeTotalPages);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, boundaryCount + 2);
  const rightSiblingIndex = Math.min(
    currentPage + siblingCount,
    safeTotalPages - boundaryCount - 1,
  );

  const shouldShowLeftEllipsis = leftSiblingIndex > boundaryCount + 2;
  const shouldShowRightEllipsis = rightSiblingIndex < safeTotalPages - boundaryCount - 1;

  const firstPages = range(1, boundaryCount);
  const lastPages = range(safeTotalPages - boundaryCount + 1, safeTotalPages);

  if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const leftRange = range(1, boundaryCount + 2 * siblingCount + 2);
    return [...leftRange, "end-ellipsis", ...lastPages];
  }

  if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
    const rightRange = range(
      safeTotalPages - (boundaryCount + 2 * siblingCount + 1),
      safeTotalPages,
    );
    return [...firstPages, "start-ellipsis", ...rightRange];
  }

  const middleRange = range(leftSiblingIndex, rightSiblingIndex);
  return [...firstPages, "start-ellipsis", ...middleRange, "end-ellipsis", ...lastPages];
};

export const Panigation: React.FC<PanigationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 2,
}) => {
  const safeCurrentPage = Math.min(Math.max(1, currentPage), Math.max(1, totalPages));
  const paginationItems = buildPaginationItems(totalPages, safeCurrentPage, Math.max(0, siblingCount));

  return (
    <div className="flex items-center justify-center gap-1 sm:gap-2 py-4 flex-wrap">
      <button
        onClick={() => onPageChange(safeCurrentPage - 1)}
        disabled={safeCurrentPage === 1}
        className="flex items-center gap-1 !px-2 sm:!px-3 !py-1.5 !text-xs sm:!text-sm !text-gray-500 dark:!text-dark-text-secondary hover:!text-gray-700 dark:hover:!text-white disabled:!opacity-50 !bg-transparent !border-none"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="hidden sm:inline">Previous</span>
      </button>
      {paginationItems.map((item, index) => {
        if (typeof item !== 'number') {
          return (
            <span
              key={`${item}-${index}`}
              className="flex items-center justify-center !w-7 sm:!w-8 !h-7 sm:!h-8 !text-xs sm:!text-sm !text-gray-400 dark:!text-dark-text-secondary"
            >
              ...
            </span>
          );
        }

        return (
          <button
            key={item}
            onClick={() => onPageChange(item)}
            className={`!w-7 sm:!w-8 !h-7 sm:!h-8 !rounded !text-xs sm:!text-sm !border-none ${
              safeCurrentPage === item
                ? '!bg-primary dark:!bg-primary-dark !text-white'
                : '!bg-transparent !text-gray-500 dark:!text-dark-text-secondary hover:!text-gray-700 dark:hover:!text-white'
            }`}
          >
            {item}
          </button>
        );
      })}
      <button
        onClick={() => onPageChange(safeCurrentPage + 1)}
        disabled={safeCurrentPage === Math.max(1, totalPages)}
        className="flex items-center gap-1 !px-2 sm:!px-3 !py-1.5 !text-xs sm:!text-sm !text-gray-500 dark:!text-dark-text-secondary hover:!text-gray-700 dark:hover:!text-white disabled:!opacity-50 !bg-transparent !border-none"
      >
        <span className="hidden sm:inline">Next</span>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};
